<template>
  <a-modal
    v-model="visible"
    title="重装 UPort 地磅串口驱动"
    :maskClosable="false"
    :keyboard="false"
    width="640px"
    :footer="null"
    @cancel="close"
  >
    <!-- ① 系统识别 -->
    <div v-if="sysLoading" class="urm-sec"><a-spin size="small" /> 正在识别您的操作系统…</div>
    <div v-else-if="sysError" class="urm-sec urm-warn">
      系统信息读取失败：{{ sysError }}（不影响重装，按默认策略执行）
    </div>
    <!-- [2026-09-26] 补 sysInfo 判空: 弹窗打开到系统信息加载完成之间的空窗期
         sysInfo 仍为 null, 裸 v-else 会渲染 sysInfo.display 报 TypeError -->
    <div v-else-if="sysInfo" class="urm-sec urm-os">
      <a-icon type="desktop" style="margin-right: 6px; color: #1890ff" />
      您的电脑是 <b>{{ sysInfo.display }}</b>
    </div>

    <!-- ② 官方策略说明 -->
    <div v-if="sysInfo" class="urm-sec urm-strategy">
      <div class="urm-strategy-title">将执行的修复方案（依据 MOXA 官方支持矩阵）：</div>
      <ul class="urm-list">
        <li>卸载当前已安装的 UPort 驱动（任意旧版本，含 1.6 / 3.1 / 3.2 / 4.3）</li>
        <li>按您的系统自动安装官方 WHQL 认证驱动（2026-09 官网最新）：
          <b>Win7 / 8 / 8.1 / 10 → v3.2</b>，<b>Windows 11 → v4.3</b>，
          两个安装包均已随好粮宝携带，无需再去官网下载</li>
        <li v-if="sysInfo.isWin11">
          您是 <b>Windows 11</b>：将自动安装官方专用驱动 <b>v4.3</b>（WHQL 认证）。
        </li>
      </ul>
      <div class="dim small">重装会先断开地磅串口连接，完成后需要把 UPort 拔出再插回，然后重新点「连接」。</div>
    </div>

    <!-- ③ 执行进度 -->
    <div v-if="running || result" class="urm-sec">
      <div class="urm-steps">
        <div v-for="(s, i) in runSteps" :key="i" :class="['urm-step', 'urm-' + s.status]">
          <span class="urm-icon">{{ stepIcon(s.status) }}</span>
          <b>{{ s.title }}</b>
          <span class="urm-summary">{{ s.summary }}</span>
        </div>
      </div>
      <a-alert
        v-if="result && result.needReboot"
        type="warning" show-icon style="margin-top: 10px"
        message="驱动要求重启电脑才能完全生效，请保存工作后重启。"
      />
    </div>

    <!-- ④ 操作按钮 -->
    <div class="urm-footer">
      <a-button :disabled="running" @click="close">关闭</a-button>
      <a-button
        v-if="result && result.success"
        type="primary"
        @click="afterSuccess"
      >我知道了，去重新插拔 UPort</a-button>
      <a-popconfirm
        v-else
        title="重装期间会断开地磅串口，确认继续？"
        :disabled="running"
        @confirm="doReinstall"
      >
        <a-button type="primary" size="large" icon="tool" :loading="running">
          {{ running ? '正在重装…（约 10~60 秒）' : '开始重装 UPort 驱动' }}
        </a-button>
      </a-popconfirm>
    </div>
  </a-modal>
</template>

<script>
/**
 * [2026-09-04] UPort 驱动一键重装弹窗 — 全局单例，挂在 MainLayout。
 *
 * 触发方：① 地磅多次连接失败的大提示（weighbridgeMixin 连续失败 ≥3 次）
 *        ② 驱动管理页 / 设备配置页的快捷按钮（store SET_UPORT_REINSTALL_VISIBLE）。
 *
 * 流程：读 /api/system/info 识别 OS（"您的电脑是 Win 几"）→ 按 MOXA 官方支持矩阵
 * 展示策略（Win11 → v4.3；Win7-10 → v3.2，两包均随好粮宝携带，按系统自动选择）
 * → POST /api/drivers/reinstall-uport（服务端卸载旧版 + 静默装官方对应版本）→ 展示步骤结果。
 */
import ScaleClient from '@/utils/scaleClient'

export default {
  name: 'UPortReinstallModal',
  data() {
    return {
      sysLoading: false,
      sysInfo: null,
      sysError: '',
      running: false,
      result: null
    }
  },
  computed: {
    visible: {
      get() { return !!(this.$store.state.deviceStatus && this.$store.state.deviceStatus.uportReinstallVisible) },
      set(v) { this.$store.commit('SET_UPORT_REINSTALL_VISIBLE', v) }
    },
    runSteps() {
      if (!this.result) return []
      return this.result.steps || []
    }
  },
  watch: {
    visible(v) {
      if (v && !this.sysInfo && !this.sysLoading) this.loadSysInfo()
    }
  },
  methods: {
    async loadSysInfo() {
      this.sysLoading = true
      this.sysError = ''
      try {
        this.sysInfo = await ScaleClient.getSystemInfo()
      } catch (e) {
        this.sysError = e.message || '外设服务不可达'
      } finally {
        this.sysLoading = false
      }
    },
    async doReinstall() {
      if (this.running) return
      this.running = true
      this.result = null
      try {
        this.result = await ScaleClient.reinstallUPort()
        if (this.result.success) {
          this.$message.success('UPort 驱动重装完成')
        } else {
          this.$message.error('UPort 驱动重装未成功，请查看步骤详情')
        }
      } catch (e) {
        this.$message.error('重装失败：' + (e.message || e.code || '未知错误'))
      } finally {
        this.running = false
      }
    },
    afterSuccess() {
      this.close()
    },
    close() {
      if (this.running) return // 重装中不允许关（卸载一半关掉没意义）
      this.$store.commit('SET_UPORT_REINSTALL_VISIBLE', false)
    },
    stepIcon(status) {
      return { pass: '✅', fail: '❌', skip: '⏭️', info: 'ℹ️' }[status] || '⏳'
    }
  }
}
</script>

<style lang="less" scoped>
.urm-sec { margin-bottom: 14px; }
.urm-os { font-size: 15px; }
.urm-warn { color: #ad6800; background: #fffbe6; padding: 8px 12px; border-radius: 4px; }
.urm-strategy {
  background: #fafafa; border: 1px solid #eee; border-radius: 6px; padding: 10px 14px;
}
.urm-strategy-title { font-weight: 600; margin-bottom: 6px; }
.urm-list { margin: 0 0 6px 0; padding-left: 20px; }
.urm-list li { line-height: 1.9; }
.urm-steps { display: flex; flex-direction: column; gap: 6px; }
.urm-step {
  display: flex; align-items: baseline; gap: 8px;
  border: 1px solid #eee; border-radius: 4px; padding: 6px 10px; background: #fafafa;
}
.urm-step.urm-pass { border-color: #b7eb8f; background: #f6ffed; }
.urm-step.urm-fail { border-color: #ffa39e; background: #fff2f0; }
.urm-summary { color: #555; }
.urm-footer { display: flex; justify-content: flex-end; gap: 10px; margin-top: 6px; }
.dim { color: #888; }
.small { font-size: 12px; }
</style>
